/**
 * UserSettingsModal — floating personal settings, triggered by `open-user-settings-modal`.
 *
 * Currently one section: My Signature — save a signature to your account (draw, type + pick a
 * handwriting font, or upload an image) for reuse across native PDF exports that need a "who
 * signed this" block (first consumer: the ASI PDF exporter, api/_asiPdf.js). Reuses
 * JourneySignaturePad.jsx (the same draw/type capture used by the in-journey client e-sign flow)
 * for the draw/type methods; upload is a plain file input converted to a data URL client-side.
 *
 * Opened by the SiteNav profile-card settings button. Escape or backdrop click to close.
 * The modal is mounted once in OutlineView and stays hidden until the event fires.
 */

import { useEffect, useState } from 'react'
import { useUser } from '@clerk/clerk-react'
import { getMySignature, saveSignature, deleteMySignature, fileToDataUrl } from '../services/userSignature.js'
import JourneySignaturePad from '../journey/JourneySignaturePad.jsx'
import { confirmDialog, alertDialog } from './Dialog.jsx'
import { useDarkMode } from '../outline/theme.js'

export default function UserSettingsModal() {
  const [open, setOpen] = useState(false)
  const [signature, setSignature] = useState(undefined) // undefined = not loaded, null = none, object = saved
  const [editing, setEditing] = useState(false)
  const [captureMode, setCaptureMode] = useState('draw') // 'draw' | 'type' | 'upload'
  const [pending, setPending] = useState(null)            // { name, dataUrl, method, font }
  const [uploadPreview, setUploadPreview] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const dark = useDarkMode()
  const { user } = useUser()

  useEffect(() => {
    function handle() { setOpen(true) }
    window.addEventListener('open-user-settings-modal', handle)
    return () => window.removeEventListener('open-user-settings-modal', handle)
  }, [])

  useEffect(() => {
    if (!open) return
    setError(null)
    if (signature === undefined) getMySignature().then(setSignature).catch(() => setSignature(null))
  }, [open]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!open) return
    function onKey(e) { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open])

  function startEdit() {
    setEditing(true); setCaptureMode('draw'); setPending(null); setUploadPreview(''); setError(null)
  }

  async function pickUploadFile(e) {
    const file = e.target.files && e.target.files[0]
    if (!file) return
    try {
      const dataUrl = await fileToDataUrl(file)
      setUploadPreview(dataUrl)
      setPending({ name: user?.fullName || '', dataUrl, method: 'upload', font: null })
    } catch { setError('Could not read that file.') }
  }

  async function save() {
    if (!pending || !pending.dataUrl) { setError('Add a signature first.'); return }
    setSaving(true); setError(null)
    try {
      const saved = await saveSignature(pending)
      setSignature(saved)
      setEditing(false)
    } catch (ex) {
      setError(ex.message || 'Failed to save signature')
    } finally {
      setSaving(false)
    }
  }

  async function remove() {
    if (!(await confirmDialog({ title: 'Remove signature?', message: 'Remove your saved signature? You can add a new one anytime.', confirmLabel: 'Remove', danger: true }))) return
    try {
      await deleteMySignature()
      setSignature(null)
    } catch (ex) { alertDialog({ title: 'Remove failed', message: ex.message || String(ex) }) }
  }

  if (!open) return null

  const backdrop = { position: 'fixed', inset: 0, zIndex: 600, background: 'rgba(7,39,45,0.48)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }
  const card = {
    background: dark ? '#0b1c24' : '#fff',
    borderRadius: 12,
    border: `0.5px solid ${dark ? 'rgba(255,255,255,0.10)' : 'rgba(7,39,45,0.14)'}`,
    boxShadow: '0 12px 36px rgba(7,39,45,0.20)',
    width: '100%', maxWidth: 520, maxHeight: '88vh', overflowY: 'auto',
    padding: '20px 20px 18px', position: 'relative',
  }
  const closeBtn = { position: 'absolute', top: 14, right: 16, background: 'none', border: 'none', cursor: 'pointer', color: dark ? 'rgba(207,226,232,0.35)' : 'rgba(7,39,45,0.30)', fontSize: 22, lineHeight: 1, padding: '2px 4px' }
  const label = { fontFamily: '"DM Mono",monospace', fontSize: 10, color: dark ? 'rgba(207,226,232,0.5)' : 'rgba(7,39,45,0.55)', letterSpacing: '0.04em', marginBottom: 6, textTransform: 'uppercase' }
  const sub = { fontFamily: '"DM Sans",sans-serif', fontSize: 12, color: dark ? 'rgba(207,226,232,0.55)' : 'rgba(7,39,45,0.5)', marginBottom: 14 }
  const ink = dark ? '#cfe2e8' : '#07272D'
  const btnGhost = { padding: '6px 14px', fontFamily: '"DM Sans",sans-serif', fontSize: 12, background: 'none', border: `0.5px solid ${dark ? 'rgba(255,255,255,0.22)' : 'rgba(7,39,45,0.22)'}`, borderRadius: 4, cursor: 'pointer', color: ink }
  const btnPrimary = { padding: '6px 16px', fontFamily: '"DM Sans",sans-serif', fontSize: 12, fontWeight: 600, background: saving ? 'rgba(7,39,45,0.18)' : '#07272D', color: saving ? 'rgba(7,39,45,0.4)' : '#fff', border: 'none', borderRadius: 4, cursor: saving ? 'default' : 'pointer' }

  return (
    <div style={backdrop} onClick={() => setOpen(false)}>
      <div data-modal-id="UserSettingsModal" style={card} onClick={e => e.stopPropagation()}>
        <button style={closeBtn} onClick={() => setOpen(false)} title="Close (Esc)">×</button>
        <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 13, fontWeight: 600, color: ink, marginBottom: 4 }}>My Signature</div>
        <div style={sub}>Saved here it's reused automatically wherever a document needs your signature — starting with issued ASIs.</div>

        {signature === undefined && <div style={{ ...sub, marginBottom: 0 }}>Loading…</div>}

        {signature && !editing && (
          <div>
            <div style={label}>Current signature</div>
            <div style={{ background: dark ? 'rgba(255,255,255,0.04)' : '#f4f7f8', border: `0.5px solid ${dark ? 'rgba(255,255,255,0.10)' : 'rgba(7,39,45,0.12)'}`, borderRadius: 6, padding: 14, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 12 }}>
              <img src={signature.image_url} alt="Your signature" style={{ maxWidth: '100%', maxHeight: 90 }} />
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button style={btnGhost} onClick={startEdit}>Replace</button>
              <button style={{ ...btnGhost, color: '#c0392b' }} onClick={remove}>Remove</button>
            </div>
          </div>
        )}

        {(signature === null || editing) && (
          <div>
            <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
              {['draw', 'type', 'upload'].map(m => (
                <button key={m} onClick={() => setCaptureMode(m)}
                  style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 12, fontWeight: captureMode === m ? 600 : 400, color: captureMode === m ? '#fff' : ink, background: captureMode === m ? '#07272D' : (dark ? 'rgba(255,255,255,0.08)' : 'rgba(7,39,45,0.06)'), border: 'none', borderRadius: 20, padding: '6px 14px', cursor: 'pointer' }}>
                  {m === 'draw' ? 'Draw' : m === 'type' ? 'Type' : 'Upload image'}
                </button>
              ))}
            </div>

            {captureMode === 'upload' ? (
              <div>
                {uploadPreview && (
                  <div style={{ background: dark ? 'rgba(255,255,255,0.04)' : '#f4f7f8', border: `0.5px solid ${dark ? 'rgba(255,255,255,0.10)' : 'rgba(7,39,45,0.12)'}`, borderRadius: 6, padding: 14, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 10 }}>
                    <img src={uploadPreview} alt="Preview" style={{ maxWidth: '100%', maxHeight: 90 }} />
                  </div>
                )}
                <label style={{ display: 'inline-block', padding: '7px 12px', fontFamily: '"DM Sans",sans-serif', fontSize: 12, color: ink, border: `0.5px solid ${dark ? 'rgba(255,255,255,0.22)' : 'rgba(7,39,45,0.22)'}`, borderRadius: 4, cursor: 'pointer' }}>
                  {uploadPreview ? 'Choose a different image' : 'Choose an image'}
                  <input type="file" accept="image/png,image/jpeg" onChange={pickUploadFile} style={{ display: 'none' }} />
                </label>
              </div>
            ) : (
              <JourneySignaturePad onChange={next => setPending({ ...next })} />
            )}

            {error && <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 11, color: '#c0392b', marginTop: 10 }}>{error}</div>}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
              {signature && <button style={btnGhost} onClick={() => setEditing(false)} disabled={saving}>Cancel</button>}
              <button style={btnPrimary} onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save signature'}</button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
